/**
 * Admin transaction surface — ONE provider at a time, named in the URL.
 *
 * Card #52886, rendering the contract #52884 publishes at `GET /api/admin/transactions/summary`.
 *
 * 🔑 A view describes EXACTLY ONE provider. No combined total is derived here, and the
 * settlement grouping uses the provider's OWN word for it — the server sends that word as
 * `settlements.label` ("Authorize.Net batches" / "Stripe payouts"). A batch is a capture cycle
 * and a payout is a bank transfer; summing them invents a number neither provider reported.
 *
 * 🔑 `available: false` IS AN ANSWER, NOT AN EMPTY RESULT. With the Stripe switch off the server
 * returns 200 carrying `unavailableReason`; rendering that as an empty table would read as "no
 * transactions" — a claim nobody made. It gets its own explicit panel.
 *
 * "Merchant" is the shared admin label for Authorize.Net; the wire value stays `authorize_net`,
 * so the URL and the API speak the same identifier. Card #53296 lifted that map into
 * web/utils/paymentProviders.js so the two order surfaces name a provider with the SAME word
 * instead of re-typing it — three copies of a label is how three surfaces start disagreeing.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatters';
import {
  PAYMENT_PROVIDERS as PROVIDERS,
  DEFAULT_PAYMENT_PROVIDER as DEFAULT_PROVIDER,
  formatProviderLabel
} from '../../utils/paymentProviders';
import styles from './TransactionsPage.module.css';
const DEFAULT_PERIOD = '7d';

const PERIODS = Object.freeze([
  Object.freeze({ value: '7d', label: 'Last 7 days' }),
  Object.freeze({ value: '30d', label: 'Last 30 days' }),
  Object.freeze({ value: '90d', label: 'Last 90 days' })
]);

const SUMMARY_TILES = Object.freeze([
  Object.freeze({ key: 'authorizedPending', label: 'Authorized / pending' }),
  Object.freeze({ key: 'captured', label: 'Captured' }),
  Object.freeze({ key: 'settlements', label: 'Settled' }),
  Object.freeze({ key: 'refunds', label: 'Refunds' }),
  Object.freeze({ key: 'voids', label: 'Voids' }),
  Object.freeze({ key: 'declines', label: 'Declines' })
]);

const IDLE = Object.freeze({ status: 'idle', report: null, error: null });

// An unknown provider resolves to the incumbent rather than rendering nothing — the same rule
// the server applies (resolveReportProvider), so a hand-edited URL cannot strand the page.
const resolveProvider = (value) =>
  PROVIDERS.some((p) => p.value === value) ? value : DEFAULT_PROVIDER;

const labelFor = (value) => formatProviderLabel(value) ?? value;

/**
 * `formatPrice` in utils/formatters.js takes CENTS; this contract publishes DOLLARS, so reusing
 * it would render every figure at 1/100th. A null amount is a bucket the provider counts but
 * does not price (voids, declines) — a dash, ⛔ never $0.00, which would be a claim.
 */
const formatAmount = (amount, currency = 'USD') =>
  amount === null || amount === undefined
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase()
      }).format(amount);

const COLUMNS = Object.freeze([
  Object.freeze({ label: 'Date', render: (r) => (r.created ? formatDateTime(r.created) : '—') }),
  Object.freeze({ label: 'Transaction', className: 'identifier', render: (r) => r.id ?? '—' }),
  Object.freeze({ label: 'Customer', render: (r) => r.customer ?? '—' }),
  Object.freeze({ label: 'Method', render: (r) => `${r.method ?? '—'}${r.last4 ? ` ····${r.last4}` : ''}` }),
  Object.freeze({ label: 'Status', render: (r, s) => <span className={s.badge}>{r.status}</span> }),
  Object.freeze({ label: 'Amount', className: 'numeric', render: (r) => formatAmount(r.amount, r.currency) })
]);

const Panel = ({ variant, heading, children, role = 'status' }) => (
  <div className={`${styles.panel} ${variant ? styles[variant] : ''}`} role={role}>
    {heading && <strong className={styles.panelHeading}>{heading}</strong>}
    {children}
  </div>
);

const ReasonList = ({ prefix, errors }) => (
  <ul className={styles.reasonList}>
    {errors.map((item, index) => <li key={`${prefix}-${index}`}>{item.message}</li>)}
  </ul>
);

const TransactionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const provider = resolveProvider(searchParams.get('provider'));
  const period = searchParams.get('period') || DEFAULT_PERIOD;

  // Keyed BY PROVIDER so each owns its loading and error state. One shared `loading` flag would
  // make Stripe's failure look like the Merchant view's failure.
  const [byProvider, setByProvider] = useState({});

  // The newest request wins. Monotonic and shared across providers: a response whose id is no
  // longer current is discarded, so a slow Merchant reply cannot overwrite a fresh Stripe one.
  const requestRef = useRef(0);
  const controllersRef = useRef({});

  const updateParam = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);
      params.set(key, value);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // The provider always appears in the URL, so every view is a deep link; this also
  // canonicalizes a hand-typed bogus value. `replace` — normalizing is not a Back-button step.
  useEffect(() => {
    if (searchParams.get('provider') !== provider) {
      const params = new URLSearchParams(searchParams);
      params.set('provider', provider);
      setSearchParams(params, { replace: true });
    }
  }, [provider, searchParams, setSearchParams]);

  const loadReport = useCallback(async (targetProvider, targetPeriod) => {
    // Switching providers ABANDONS the other one's in-flight request rather than letting it land.
    Object.entries(controllersRef.current).forEach(([key, controller]) => {
      if (key !== targetProvider) {
        controller.abort();
        delete controllersRef.current[key];
      }
    });

    const controller = new AbortController();
    controllersRef.current[targetProvider] = controller;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setByProvider((prev) => ({
      ...prev,
      [targetProvider]: { ...(prev[targetProvider] ?? IDLE), status: 'loading', error: null }
    }));

    try {
      const report = await api.get('/admin/transactions/summary', {
        params: { provider: targetProvider, period: targetPeriod },
        signal: controller.signal
      });
      if (requestId !== requestRef.current) return;
      setByProvider((prev) => ({ ...prev, [targetProvider]: { status: 'loaded', report, error: null } }));
    } catch (error) {
      // A cancelled request is not a failure — it must never paint an error the user caused by
      // clicking the other tab.
      if (controller.signal.aborted || error?.name === 'AbortError') return;
      if (requestId !== requestRef.current) return;
      setByProvider((prev) => ({
        ...prev,
        [targetProvider]: { status: 'error', report: null, error: error?.message || 'Unable to load transactions.' }
      }));
    } finally {
      if (controllersRef.current[targetProvider] === controller) delete controllersRef.current[targetProvider];
    }
  }, []);

  useEffect(() => { loadReport(provider, period); }, [provider, period, loadReport]);

  // Unmount must not leave a request writing into a dead tree.
  useEffect(() => {
    const controllers = controllersRef.current;
    return () => Object.values(controllers).forEach((controller) => controller.abort());
  }, []);

  const { status, report, error } = byProvider[provider] ?? IDLE;
  const unavailable = report && report.available === false;
  const settlements = report?.settlements;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Transactions</h1>
          <p className={styles.subtitle}>
            Payment activity for one provider at a time. Settlement groupings keep each
            provider&apos;s own name and are never combined.
          </p>
        </div>
      </header>

      <div className={styles.controls}>
        <div className={styles.segmented} role="group" aria-label="Payment provider">
          {PROVIDERS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.segment} ${option.value === provider ? styles.segmentActive : ''}`}
              aria-pressed={option.value === provider}
              onClick={() => updateParam('provider', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <label className={styles.periodLabel}>
          <span className={styles.periodCaption}>Period</span>
          <select
            className={styles.select}
            value={period}
            onChange={(event) => updateParam('period', event.target.value)}
          >
            {PERIODS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      {status === 'loading' && <Panel>Loading {labelFor(provider)} transactions…</Panel>}

      {status === 'error' && (
        <Panel variant="panelError" role="alert" heading={`${labelFor(provider)} transactions could not be loaded`}>
          <span className={styles.panelBody}>{error}</span>
        </Panel>
      )}

      {status === 'loaded' && unavailable && (
        <Panel variant="panelUnavailable" heading={`${labelFor(provider)} is unavailable`}>
          <span className={styles.panelBody}>
            {report.unavailableReason || `${labelFor(provider)} is not available.`}
          </span>
          {report.errors?.length > 0 && <ReasonList prefix="unavailable" errors={report.errors} />}
        </Panel>
      )}

      {status === 'loaded' && !unavailable && report && (
        <>
          <section className={styles.summaryGrid} aria-label="Summary">
            {SUMMARY_TILES.map((tile) => (
              <div key={tile.key} className={styles.tile}>
                <span className={styles.tileLabel}>{tile.label}</span>
                <span className={styles.tileAmount}>{formatAmount(report.summary?.[tile.key]?.amount)}</span>
                <span className={styles.tileCount}>{report.summary?.[tile.key]?.count ?? 0} transactions</span>
              </div>
            ))}
          </section>

          <section className={styles.section} aria-label="Settlements">
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>{settlements?.label}</h2>
              <span className={styles.sectionMeta}>
                {settlements?.count ?? 0} · {formatAmount(settlements?.amount)}
              </span>
            </div>
            {settlements?.items?.length > 0 ? (
              <ul className={styles.settlementList}>
                {settlements.items.map((item, index) => (
                  <li key={item.id ?? `settlement-${index}`} className={styles.settlementRow}>
                    <span className={styles.settlementId}>{item.id ?? '—'}</span>
                    <span className={styles.settlementMeta}>
                      {item.settledAt ? formatDateTime(item.settledAt) : '—'}
                      {item.state ? ` · ${item.state}` : ''}
                    </span>
                    <span className={styles.settlementAmount}>
                      {formatAmount(item.amount)} · {item.count ?? 0}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.empty}>No {settlements?.label?.toLowerCase()} in this period.</p>
            )}
          </section>

          <section className={styles.section} aria-label="Transactions">
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Transactions</h2>
              <span className={styles.sectionMeta}>{report.transactions?.length ?? 0} shown</span>
            </div>
            {report.transactions?.length > 0 ? (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {COLUMNS.map((column) => (
                        <th key={column.label} scope="col" className={column.className ? styles[column.className] : ''}>
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.transactions.map((row, index) => (
                      <tr key={row.id ?? `transaction-${index}`}>
                        {COLUMNS.map((column) => (
                          <td key={column.label} className={column.className ? styles[column.className] : ''}>
                            {column.render(row, styles)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.empty}>No transactions in this period.</p>
            )}
          </section>

          {report.partial && report.errors?.length > 0 && (
            <Panel variant="panelWarning" heading="Partial result">
              <ReasonList prefix="partial" errors={report.errors} />
            </Panel>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionsPage;
